import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEO from '@/components/feature/SEO';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, type Category } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug) as Category | undefined;

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-semibold text-foreground-900 mb-2">分类不存在</h2>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === slug);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category.name },
  ];

  const listSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 一本道`,
    description: category.description,
    url: `https://yibendao.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '一本道',
      url: 'https://yibendao.cn',
    },
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${category.name} - 一本道`}
        description={category.description}
        keywords={`一本道,${category.name},一本道${category.name},一本道成长,一本道人生哲学`}
        canonicalUrl={`https://yibendao.cn/category/${slug}`}
        ogType="website"
      />
      <SchemaOrg schema={listSchema} />

      <Navbar />
      <main className="pt-20 pb-16">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-4 mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-lg`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900">
                {category.name}
              </h1>
            </div>
            <p className="text-sm text-foreground-500 leading-relaxed max-w-2xl">
              {category.description}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="space-y-8">
            {categoryArticles.length > 0 ? (
              categoryArticles.map((article) => (
                <article key={article.slug} className="group">
                  <Link to={`/article/${article.slug}`} className="block">
                    <div className="flex flex-col sm:flex-row gap-5 p-4 -mx-4 rounded-lg hover:bg-background-100 transition-colors duration-200">
                      <div className="relative w-full sm:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1.5">
                          <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
                          <span className="text-xs text-foreground-300">·</span>
                          <span className="text-xs text-foreground-400">{article.publishedAt}</span>
                        </div>
                        <h2 className="font-heading text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 leading-snug mb-1.5">
                          {article.title}
                        </h2>
                        <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {article.tags.map((tag) => (
                            <span
                              key={tag}
                              className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded whitespace-nowrap"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Link>
                </article>
              ))
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}